// Time handling for schedule entry and display (UX-DESIGN.md §2.3 step 1,
// §2.4 "Schedule display states the stored instant plainly").
//
// Local times are interpreted in the viewer's own time zone (the display
// time zone is always named on screen). A local time that does not exist
// (spring-forward gap) is rejected; one that exists twice (fall-back
// overlap) forces an explicit choice of offset. Nothing is ever silently
// shifted to "the nearest valid time".

export function viewerTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "local time";
  } catch {
    return "local time";
  }
}

export interface LocalDateTimeParts {
  year: number;
  month: number; // 1-12
  day: number;
  hour: number;
  minute: number;
}

/** Parses the value of an <input type="datetime-local"> ("2026-03-08T02:30"). */
export function parseLocalInput(value: string): LocalDateTimeParts | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::\d{2}(?:\.\d+)?)?$/.exec(value);
  if (!m) return null;
  return { year: +m[1]!, month: +m[2]!, day: +m[3]!, hour: +m[4]!, minute: +m[5]! };
}

export type LocalResolution =
  | { kind: "ok"; instant: Date }
  | { kind: "skipped" }
  | { kind: "ambiguous"; candidates: [Date, Date] }
  | { kind: "invalid" };

function matches(d: Date, p: LocalDateTimeParts): boolean {
  return (
    d.getFullYear() === p.year &&
    d.getMonth() + 1 === p.month &&
    d.getDate() === p.day &&
    d.getHours() === p.hour &&
    d.getMinutes() === p.minute
  );
}

/**
 * Resolves a local wall-clock time in the viewer's zone to UTC instants.
 * Candidate offsets are taken from the zone's offsets a day either side,
 * which covers every real-world DST transition.
 */
export function resolveLocal(p: LocalDateTimeParts): LocalResolution {
  const naiveUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute);
  if (Number.isNaN(naiveUtc)) return { kind: "invalid" };
  const offsets = new Set<number>();
  for (const delta of [-36, -12, 0, 12, 36]) {
    offsets.add(new Date(naiveUtc + delta * 3_600_000).getTimezoneOffset());
  }
  const found = new Map<number, Date>();
  for (const off of offsets) {
    const candidate = new Date(naiveUtc + off * 60_000);
    if (matches(candidate, p)) found.set(candidate.getTime(), candidate);
  }
  const list = [...found.values()].sort((a, b) => a.getTime() - b.getTime());
  if (list.length === 0) return { kind: "skipped" };
  if (list.length === 1) return { kind: "ok", instant: list[0]! };
  return { kind: "ambiguous", candidates: [list[0]!, list[list.length - 1]!] };
}

/** RFC 3339 UTC with seconds, what create_campaign expects (advertiser.go:147). */
export function toRfc3339Utc(d: Date): string {
  return d.toISOString().replace(/\.\d{3}Z$/, "Z");
}

/** "Thu, Mar 5, 2026, 2:14 PM EST" in the viewer's zone. */
export function formatLocal(iso: string | Date): string {
  const d = typeof iso === "string" ? new Date(iso) : iso;
  if (Number.isNaN(d.getTime())) return String(iso);
  return new Intl.DateTimeFormat(undefined, {
    weekday: "short",
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "short",
  }).format(d);
}

/** Plain UTC instant, e.g. "2026-03-05 19:14:00 UTC". */
export function formatUtc(iso: string | Date): string {
  const d = typeof iso === "string" ? new Date(iso) : iso;
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toISOString().replace("T", " ").replace(/\.\d{3}Z$/, " UTC");
}

export function formatOffset(d: Date): string {
  const off = -d.getTimezoneOffset();
  const sign = off >= 0 ? "+" : "-";
  const abs = Math.abs(off);
  return `UTC${sign}${String(Math.floor(abs / 60)).padStart(2, "0")}:${String(abs % 60).padStart(2, "0")}`;
}

/** Date-only range label for statement periods ("period_end_exclusive"). */
export function formatPeriod(startIso: string, endExclusiveIso: string): string {
  const start = new Date(startIso);
  const endExcl = new Date(endExclusiveIso);
  if (Number.isNaN(start.getTime()) || Number.isNaN(endExcl.getTime())) return `${startIso} – ${endExclusiveIso}`;
  const endInclusive = new Date(endExcl.getTime() - 1);
  const fmt = new Intl.DateTimeFormat(undefined, { year: "numeric", month: "short", day: "numeric", timeZone: "UTC" });
  return `${fmt.format(start)} – ${fmt.format(endInclusive)} (UTC)`;
}
